import * as Schema from "effect/Schema";

/**
 * PR J (step-3 switchboard, ea's ruling): a collection in the Context Rail
 * shows its rows through the EXISTING `collection.records` reader (the Canvas
 * door's `collection-query`, backend queryRecordsForAccess), read under the
 * person's own grant, so the rail never shows a row they cannot read. One
 * bounded page at a time, continued by the reader's own cursor; a deleted
 * collection, or one the person cannot read, is unavailable, never empty.
 *
 * This shape travels only between this Desk's server and its own page
 * (`/api/neucleos/collection/rows`). It adds no field and no literal to any
 * shape a World or an installed Desk already decodes: the rail's catalog
 * still lists a collection as the same `world-convex:collection/<id>` member.
 */
export const COLLECTION_ROWS_SCHEMA_VERSION = "collection-rows-v1";
/** Rows in one page (the reader allows 1 to 50). */
export const COLLECTION_ROWS_PAGE_SIZE = 25;
/** The most declared fields one view labels. */
export const COLLECTION_ROWS_MAX_FIELDS = 64;

const COLLECTION_ID = /^ccl_[a-f0-9]{24}$/u;
const COLLECTION_MEMBER = /^world-convex:collection\/(ccl_[a-f0-9]{24})$/u;

export const isCollectionId = (value: unknown): value is string =>
  typeof value === "string" && COLLECTION_ID.test(value);
/** The collection a rail member names, or null when it names none. */
export const collectionIdOfMember = (memberId: string): string | null =>
  COLLECTION_MEMBER.exec(memberId)?.[1] ?? null;

export const CollectionRowsField = Schema.Struct({
  /** The declared property name. */
  name: Schema.String.check(Schema.isMaxLength(64)),
  /** The schema's own label for it (its `title`), else its name. */
  label: Schema.String.check(Schema.isMaxLength(160)),
});
export type CollectionRowsField = typeof CollectionRowsField.Type;

export const CollectionRowsRow = Schema.Struct({
  recordId: Schema.String.check(Schema.isMaxLength(128)),
  /** Only the declared fields the record holds. */
  values: Schema.Record(Schema.String, Schema.Unknown),
});
export type CollectionRowsRow = typeof CollectionRowsRow.Type;

export const CollectionRowsView = Schema.Struct({
  schemaVersion: Schema.Literal(COLLECTION_ROWS_SCHEMA_VERSION),
  collectionId: Schema.String.check(Schema.isPattern(COLLECTION_ID)),
  /** False: deleted, or not readable by this person. Nothing else is said. */
  available: Schema.Boolean,
  name: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(160))),
  fields: Schema.optionalKey(
    Schema.Array(CollectionRowsField).check(Schema.isMaxLength(COLLECTION_ROWS_MAX_FIELDS)),
  ),
  rows: Schema.optionalKey(Schema.Array(CollectionRowsRow).check(Schema.isMaxLength(50))),
  /** The reader's own continuation; null on the last page. */
  nextCursor: Schema.optionalKey(Schema.NullOr(Schema.String.check(Schema.isMaxLength(16000)))),
});
export type CollectionRowsView = typeof CollectionRowsView.Type;

export const unavailableCollectionRows = (collectionId: string): CollectionRowsView => ({
  schemaVersion: COLLECTION_ROWS_SCHEMA_VERSION,
  collectionId,
  available: false,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isVersion = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 1;

/** The collection schema version an answer was read at, or null. */
export const collectionSchemaVersionOf = (answer: unknown): number | null =>
  isRecord(answer) && isVersion(answer["schemaVersion"]) ? answer["schemaVersion"] : null;

/**
 * Whether a `collection-query` answer is one page of THIS collection: at most
 * 50 records, each of this collection with a record id and values, a
 * continuation that is null or a non-empty string, and its schema version.
 * Checked before anything else is compared (ea's review of #1763), so a
 * malformed answer is refused whatever its version says.
 */
export function isCollectionPage(
  collectionId: string,
  page: unknown,
): page is Record<string, unknown> {
  if (
    !isRecord(page) ||
    !Array.isArray(page["records"]) ||
    page["records"].length > 50 ||
    !isVersion(page["schemaVersion"])
  )
    return false;
  const nextCursor = page["nextCursor"];
  if (
    nextCursor !== null &&
    (typeof nextCursor !== "string" || nextCursor.length === 0 || nextCursor.length > 16000)
  )
    return false;
  return page["records"].every(
    (record) =>
      isRecord(record) &&
      record["collectionId"] === collectionId &&
      typeof record["recordId"] === "string" &&
      isRecord(record["values"]),
  );
}

/**
 * The rail's view of one collection from the two Canvas door answers it
 * reads: `collection-read` (name, schema, deleted status) and
 * `collection-query` (one page of records). Null when either answer is not
 * the shape those reads return (isCollectionPage), or when the page was read
 * at another schema version than the description, so one schema's labels are
 * never shown over another's rows (the caller reads the description again
 * first; ea's review of #1763).
 */
export function projectCollectionRows(
  collectionId: string,
  described: unknown,
  page: unknown,
): CollectionRowsView | null {
  if (!isRecord(described) || described["collectionId"] !== collectionId) return null;
  if (described["status"] === "deleted") return unavailableCollectionRows(collectionId);
  const schema = described["schema"];
  const name = described["name"];
  if (
    typeof name !== "string" ||
    !isRecord(schema) ||
    !isRecord(schema["properties"]) ||
    !isVersion(described["schemaVersion"])
  )
    return null;
  const fields = Object.entries(schema["properties"])
    .slice(0, COLLECTION_ROWS_MAX_FIELDS)
    .map(([field, declaration]) => {
      const title = isRecord(declaration) ? declaration["title"] : undefined;
      return {
        name: field.slice(0, 64),
        label: (typeof title === "string" && title.trim() ? title.trim() : field).slice(0, 160),
      };
    });
  if (!isCollectionPage(collectionId, page) || page["schemaVersion"] !== described["schemaVersion"])
    return null;
  const nextCursor = page["nextCursor"] as string | null;
  const rows: Array<CollectionRowsRow> = [];
  for (const record of page["records"] as Array<Record<string, unknown>>) {
    if (record["tombstone"] === true) continue;
    const values = record["values"] as Record<string, unknown>;
    rows.push({
      recordId: (record["recordId"] as string).slice(0, 128),
      values: Object.fromEntries(
        fields
          .filter((field) => Object.hasOwn(values, field.name))
          .map((field) => [field.name, values[field.name]]),
      ),
    });
  }
  return {
    schemaVersion: COLLECTION_ROWS_SCHEMA_VERSION,
    collectionId,
    available: true,
    name: name.slice(0, 160),
    fields,
    rows,
    nextCursor,
  };
}
